<template>
  <!-- 两档布局：<768px 移动 H5；≥768px 统一桌面双栏（本节点渲染） -->
  <div class="desktop-stage pc-layout">
    <div class="page-shell">
      <div class="ambient ambient-one"></div>
      <div class="ambient ambient-two"></div>

      <div class="topbar">
        <div class="topbar-left">
          <span class="eyebrow">AI FACE CHALLENGE</span>
          <span class="brand">表情大考验</span>
        </div>
        <div class="score-pill">
          <span class="score-icon">★</span>
          <span>{{ bestScore }}</span>
        </div>
      </div>

      <div v-if="stage === 'ready'" class="ready-stage">
        <div class="hero-copy">
          <span class="hero-title">AI 看你的表情</span>
          <span class="hero-subtitle">10 张表情连拍，AI 自动评分取平均</span>
        </div>

        <div class="expression-orbit">
          <div class="orbit-ring ring-one"></div>
          <div class="orbit-ring ring-two"></div>
          <span class="spark spark-a">✦</span>
          <span class="spark spark-b">✦</span>
          <span class="spark spark-c">·</span>
          <div class="expression-main">
            <img class="main-expression-image" :src="surpriseImg" alt />
            <span class="main-emoji-glow">✦</span>
          </div>
          <div class="expression-float float-left">
            <img :src="happyImg" alt />
            <span class="float-label">开心</span>
          </div>
          <div class="expression-float float-right">
            <img :src="winkImg" alt />
            <span class="float-label">眨眼</span>
          </div>
          <div class="expression-float float-bottom">
            <img :src="poutImg" alt />
            <span class="float-label">嘟嘴</span>
          </div>
        </div>

        <div class="rule-card">
          <div class="rule-row">
            <div class="rule-icon purple">10</div>
            <div class="rule-text">
              <span class="rule-title">十连拍挑战</span>
              <span class="rule-desc">每张 3 秒自动拍照评分，共 10 张</span>
            </div>
          </div>
          <div class="rule-row">
            <div class="rule-icon orange">AI</div>
            <div class="rule-text">
              <span class="rule-title">平均得分</span>
              <span class="rule-desc">10 次 AI 总分取平均，保留 1 位小数</span>
            </div>
          </div>
        </div>

        <button class="primary-button" @click="startChallenge">
          开始挑战
          <span class="button-arrow">→</span>
        </button>
        <span class="hint-text">共 10 张表情 · 每 3 秒自动拍照 · AI 评分取平均分</span>
      </div>

      <div v-else-if="stage === 'playing'" class="playing-stage">
        <div class="camera-card">
          <video
            ref="cameraVideo"
            class="live-camera"
            :class="{ 'is-hidden': !cameraReady }"
            autoplay
            muted
            playsinline
          ></video>
          <img
            v-if="!cameraReady"
            class="camera-face camera-fallback"
            :src="currentExpression.image"
            alt
          />
          <div class="camera-corner corner-tl"></div>
          <div class="camera-corner corner-tr"></div>
          <div class="camera-corner corner-bl"></div>
          <div class="camera-corner corner-br"></div>
          <div class="scan-line"></div>
          <div class="camera-status">
            <div class="status-dot" :class="{ 'status-error': cameraError }"></div>
            <span>{{ cameraError || faceStatus }}</span>
          </div>
          <div v-if="prepCount > 0" class="prep-overlay">
            <div class="prep-circle"><span>{{ prepCount }}</span></div>
            <span class="prep-text">请准备好你的表情</span>
          </div>
        </div>
        <div class="playing-controll">
          <div class="challenge-head">
            <div class="challenge-head-text">
              <span class="section-label">{{ roundLabel }}</span>
              <div class="challenge-title-row">
                <span class="challenge-title">{{ currentExpression.name }}</span>
                <img class="challenge-expression-image" :src="currentExpression.image" alt />
              </div>
              <span class="challenge-action">{{ currentExpression.action }}</span>
              <span class="face-mode-tip">每 3 秒自动拍照评分 · 共 {{ totalRounds }} 张取平均分</span>
            </div>
            <div class="timer" :class="{ 'timer-warning': remaining <= 10 }">
              <span class="timer-number">{{ remaining }}</span>
              <span class="timer-unit">秒</span>
            </div>
          </div>
          <div class="live-score-card">
            <div class="live-score-head">
              <span>{{ scoreHeadLabel }}</span>
              <span class="live-score-value">{{ liveScore }}%</span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: liveScore + '%' }"></div>
            </div>
            <div class="metrics">
              <div class="metric">
                <span class="metric-icon">◉</span>
                <span>眼睛</span>
                <span class="metric-value">{{ metrics.eyes }}%</span>
              </div>
              <div class="metric">
                <span class="metric-icon">◒</span>
                <span>嘴巴</span>
                <span class="metric-value">{{ metrics.mouth }}%</span>
              </div>
              <div class="metric">
                <span class="metric-icon">⌁</span>
                <span>眉毛</span>
                <span class="metric-value">{{ metrics.brows }}%</span>
              </div>
            </div>
          </div>
          <div class="camera-actions">
            <button
              class="camera-action camera-shot"
              :disabled="isScoring || prepCount > 0 || roundIndex >= totalRounds"
              @click="
                !cameraReady
                  ? retryStartCamera()
                  : roundIndex < 0
                  ? startChallenge()
                  : forceCaptureNow()
              "
            >
              <template v-if="isScoring">…<span>AI 评分中</span></template>
              <template v-else-if="prepCount > 0">◉<span>准备中…</span></template>
              <template v-else-if="!cameraReady">◉<span>开启摄像头</span></template>
              <template v-else-if="roundIndex < 0">▶<span>开始挑战</span></template>
              <template v-else>●<span>立即拍照</span></template>
            </button>
          </div>
        </div>
      </div>

      <div v-else class="result-stage">
        <div class="result-badge">AI SCORE</div>
        <span class="result-title">{{ resultComment }}</span>
        <span class="result-desc">{{ resultTip }}</span>
        <div class="result-score">
          <span class="result-number">{{ finalScore }}</span>
          <span class="result-unit">分</span>
        </div>
        <div class="result-metrics">
          <div class="result-metric">
            <span>{{ resultMetrics.eyes }}%</span>
            <span>眼睛</span>
          </div>
          <div class="result-metric">
            <span>{{ resultMetrics.mouth }}%</span>
            <span>嘴巴</span>
          </div>
          <div class="result-metric">
            <span>{{ resultMetrics.brows }}%</span>
            <span>眉毛</span>
          </div>
        </div>
        <div class="result-tip">
          <span class="tip-label">最后一个表情</span>
          <div class="result-tip-row">
            <img :src="lastExpression.image" alt />
            <span>{{ lastExpression.name }} · {{ lastExpression.level }}</span>
          </div>
        </div>
        <button class="primary-button" @click="startChallenge">
          再来一次
          <span class="button-arrow">↻</span>
        </button>
        <button class="ghost-button" @click="backHome">返回首页</button>
      </div>
    </div>
  </div>
</template>

<script>
import surpriseSvg from "@/assets/expressions/surprise.svg";
import happySvg from "@/assets/expressions/happy.svg";
import fearSvg from "@/assets/expressions/fear.svg";
import angrySvg from "@/assets/expressions/angry.svg";
import sadSvg from "@/assets/expressions/sad.svg";
import winkSvg from "@/assets/expressions/wink.svg";
import shySvg from "@/assets/expressions/shy.svg";
import sleepySvg from "@/assets/expressions/sleepy.svg";
import confidentSvg from "@/assets/expressions/confident.svg";
import confusedSvg from "@/assets/expressions/confused.svg";
import crySvg from "@/assets/expressions/cry.svg";
import poutSvg from "@/assets/expressions/pout.svg";
import disgustSvg from "@/assets/expressions/disgust.svg";
import smirkSvg from "@/assets/expressions/smirk.svg";
import kissSvg from "@/assets/expressions/kiss.svg";
import { nextTick } from "vue";
import { showToast } from "vant";

const expressions = [
  {
    name: "超级惊讶",
    level: "进阶",
    image: surpriseSvg,
    action: "眉毛抬高 + 眼睛睁大 + 嘴巴张圆",
    target: { eyes: 0.92, mouth: 0.76, brows: 0.88 }
  },
  {
    name: "开心大笑",
    level: "进阶",
    image: happySvg,
    action: "眼睛弯起 + 嘴角上扬 + 露出上排牙齿",
    target: { eyes: 0.36, mouth: 0.58, brows: 0.35 }
  },
  {
    name: "震惊恐惧",
    level: "挑战",
    image: fearSvg,
    action: "眉毛上挑 + 眼睛睁大 + 下巴放松",
    target: { eyes: 0.96, mouth: 0.82, brows: 0.82 }
  },
  {
    name: "认真生气",
    level: "挑战",
    image: angrySvg,
    action: "眉心收紧 + 目光集中 + 嘴角下压",
    target: { eyes: 0.2, mouth: 0.12, brows: 0.12 }
  },
  {
    name: "委屈悲伤",
    level: "进阶",
    image: sadSvg,
    action: "眉头上扬 + 眼睛含泪 + 嘴角向下",
    target: { eyes: 0.2, mouth: 0.16, brows: 0.76 }
  },
  {
    name: "俏皮眨眼",
    level: "挑战",
    image: winkSvg,
    action: "一只眼闭紧 + 另一只眼睁开 + 嘴角上扬",
    target: { eyes: 0.5, mouth: 0.22, brows: 0.38 }
  },
  {
    name: "害羞脸红",
    level: "进阶",
    image: shySvg,
    action: "视线下移 + 嘴角轻抿 + 面颊收紧",
    target: { eyes: 0.28, mouth: 0.1, brows: 0.4 }
  },
  {
    name: "困困打哈欠",
    level: "进阶",
    image: sleepySvg,
    action: "眼皮下垂 + 嘴巴张开 + 表情放松",
    target: { eyes: 0.1, mouth: 0.58, brows: 0.25 }
  },
  {
    name: "自信微笑",
    level: "挑战",
    image: confidentSvg,
    action: "眼神坚定 + 单侧嘴角上扬 + 下巴微抬",
    target: { eyes: 0.42, mouth: 0.18, brows: 0.5 }
  },
  {
    name: "疑惑不解",
    level: "挑战",
    image: confusedSvg,
    action: "一边眉毛抬高 + 眼睛微眯 + 嘴角偏向一侧",
    target: { eyes: 0.28, mouth: 0.18, brows: 0.55 }
  },
  {
    name: "伤心大哭",
    level: "挑战",
    image: crySvg,
    action: "眉头上扬 + 眼睛眯起 + 嘴巴向下撇",
    target: { eyes: 0.16, mouth: 0.48, brows: 0.86 }
  },
  {
    name: "嘟嘴生气",
    level: "进阶",
    image: poutSvg,
    action: "双唇前突 + 眉心收紧 + 鼻翼微张",
    target: { eyes: 0.3, mouth: 0.08, brows: 0.15 }
  },
  {
    name: "嫌弃皱鼻",
    level: "挑战",
    image: disgustSvg,
    action: "鼻子皱起 + 上唇抬高 + 眼睛微眯",
    target: { eyes: 0.22, mouth: 0.14, brows: 0.28 }
  },
  {
    name: "一边憋笑",
    level: "挑战",
    image: smirkSvg,
    action: "嘴角单侧上扬 + 眼睛微眯 + 忍住笑意",
    target: { eyes: 0.28, mouth: 0.16, brows: 0.32 }
  },
  {
    name: "撅嘴飞吻",
    level: "进阶",
    image: kissSvg,
    action: "双唇撅起 + 面颊收紧 + 轻轻眨眼",
    target: { eyes: 0.34, mouth: 0.1, brows: 0.32 }
  }
];

/* ------- 核心玩法常量：10 连拍，每张 3 秒自动 AI 评分，取平均 ------- */
const TOTAL_ROUNDS = 10;
const qs = new URLSearchParams(window.location.search);
const queryInt = (name, fallback, min, max) => {
  const v = Number(qs.get(name));
  if (!v || Number.isNaN(v)) return fallback;
  return Math.max(min, Math.min(max, v));
};
const ROUND_MS = queryInt("roundMs", 3000, 120, 60000); // 每张展示时长
const PREP_STEP_MS = queryInt("prepMs", 1000, 60, 3000); // 预备倒计时每格时长
const AI_OFF = qs.get("ai") === "off"; // ?ai=off 跳过真实接口,纯本地模拟(演示/测试用)

/* 评分严格系数（真实接口与本地模拟共用同一套）：按表情难度压分 */
const STRICT_FACTOR = { 进阶: 0.75, 挑战: 0.68 };

export default {
  name: "FaceGame",
  data() {
    return {
      stage: (() => {
        const s = new URLSearchParams(window.location.search).get("stage");
        return ["ready", "playing", "result"].includes(s) ? s : "ready";
      })(),
      remaining: 30,
      timer: null,
      cameraStream: null,
      cameraReady: false,
      faceStatus: "准备开始挑战",
      metrics: { eyes: 0, mouth: 0, brows: 0 },
      resultMetrics: { eyes: 0, mouth: 0, brows: 0 },
      currentExpression: expressions[0],
      lastExpression: expressions[0],
      liveScore: 0,
      finalScore: 0,
      resultComment: "",
      resultTip: "",
      bestScore: Number(window.localStorage.getItem("face-game-best") || 0),
      cameraError: "",
      isScoring: false,
      prepCount: 0,
      roundIndex: -1,
      totalRounds: TOTAL_ROUNDS,
      expressionList: [],
      roundScores: [],
      surpriseImg: surpriseSvg,
      happyImg: happySvg,
      winkImg: winkSvg,
      poutImg: poutSvg
    };
  },
  computed: {
    roundLabel() {
      if (this.prepCount > 0)
        return `准备中 · 第 1 / ${this.totalRounds} 个表情`;
      if (this.roundIndex < 0) return "即将开始挑战";
      return `第 ${this.roundIndex + 1} / ${this.totalRounds} 个表情 · ${
        this.currentExpression.level
      }`;
    },
    scoreHeadLabel() {
      if (this.prepCount > 0 || this.roundIndex < 0) return "实时匹配度";
      return `第 ${this.roundIndex + 1} 张 AI 得分`;
    }
  },
  mounted() {
    // 两档布局：<768px 移动 H5；≥768px 由 CSS 媒体查询切到桌面双栏
  },
  beforeUnmount() {
    this.clearTimers();
    this.stopCamera();
  },
  methods: {
    clearTimers() {
      clearInterval(this.timer);
      clearTimeout(this._prepTimer);
      clearTimeout(this._captureTimer);
      clearTimeout(this._roundTimer);
      this.timer = null;
      this._prepTimer = null;
      this._captureTimer = null;
      this._roundTimer = null;
    },
    async startCamera() {
      const isLocalhost = ["localhost", "127.0.0.1", "[::1]"].includes(
        window.location.hostname
      );
      const isSecureContext = window.isSecureContext || isLocalhost;

      if (!isSecureContext) {
        this.cameraError = "HTTP 页面无法打开摄像头";
        this.faceStatus = "请使用 HTTPS 地址访问，或在本机 localhost 调试";
        return;
      }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = "当前浏览器不支持摄像头";
        this.faceStatus = "请使用最新版 Chrome、Safari 或 Edge";
        return;
      }

      try {
        this.cameraError = "";
        this.faceStatus = "正在请求摄像头权限...";
        this.cameraStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: { ideal: 720 },
            height: { ideal: 960 }
          },
          audio: false
        });
        const video = this.$refs.cameraVideo;
        if (video) {
          video.srcObject = this.cameraStream;
          await video.play();
        }
        this.cameraReady = true;
        this.faceStatus = "摄像头已开启，正在模拟识别...";
      } catch (error) {
        this.cameraReady = false;
        const cameraMessages = {
          NotAllowedError: "摄像头权限被拒绝，请在浏览器设置中允许",
          PermissionDeniedError: "摄像头权限被拒绝，请在浏览器设置中允许",
          NotFoundError: "没有检测到可用摄像头",
          NotReadableError: "摄像头可能被其他应用占用",
          OverconstrainedError: "摄像头不满足当前设备要求",
          SecurityError: "当前页面没有摄像头访问权限"
        };
        this.cameraError =
          cameraMessages[error.name] || "摄像头暂不可用，请检查设备权限";
        this.faceStatus = "无法获取摄像头画面";
      }
    },
    stopCamera() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop());
        this.cameraStream = null;
      }
      this.cameraReady = false;
    },
    // 本局 10 个表情：洗牌后取前 10，保证一局内不重复（池共 15 个）
    buildRoundList() {
      const pool = expressions.slice();
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      return pool.slice(0, TOTAL_ROUNDS);
    },
    async startChallenge() {
      if (this._startingChallenge) return;
      this._startingChallenge = true;
      try {
        this.clearTimers();
        this.stopCamera();
        this.stage = "playing";
        this.prepCount = 0;
        this.roundIndex = -1;
        this._busy = 0;
        this._fallbackWarned = false;
        this.expressionList = this.buildRoundList();
        this.currentExpression = this.expressionList[0];
        this.lastExpression = this.expressionList[0];
        this.remaining = 30;
        this.liveScore = 0;
        this.metrics = { eyes: 0, mouth: 0, brows: 0 };
        this.roundScores = new Array(TOTAL_ROUNDS).fill(null);
        this.cameraError = "";
        this.cameraReady = false;
        this.isScoring = false;
        this.faceStatus = "正在开启摄像头...";
        await nextTick();
        await this.startCamera();
        // 有摄像头：3 秒预备倒计时后自动开始 10 连拍；无摄像头：停留挑战页提示重试
        if (this.cameraReady) {
          this.beginPrep();
        } else {
          this.faceStatus =
            (this.cameraError || "未检测到摄像头") +
            "：请点击下方「开启摄像头」重试";
        }
      } finally {
        this._startingChallenge = false;
      }
    },
    async retryStartCamera() {
      if (this._startingChallenge) return;
      this._startingChallenge = true;
      try {
        this.cameraError = "";
        this.faceStatus = "正在开启摄像头...";
        await this.startCamera();
        if (this.cameraReady) {
          this.beginPrep();
        } else {
          this.faceStatus =
            (this.cameraError || "未检测到摄像头") +
            "：请点击下方「开启摄像头」重试";
        }
      } finally {
        this._startingChallenge = false;
      }
    },
    // 3-2-1 预备倒计时(覆盖在摄像头画面上),结束后出第 1 张表情
    beginPrep() {
      this.prepCount = 3;
      this.faceStatus = "请面对摄像头，倒计时结束后自动开始 10 连拍";
      this._prepTimer = setInterval(() => {
        this.prepCount -= 1;
        if (this.prepCount <= 0) {
          this.prepCount = 0;
          clearInterval(this._prepTimer);
          this._prepTimer = null;
          this.beginRounds();
        }
      }, PREP_STEP_MS);
    },
    beginRounds() {
      this.remaining = 30;
      this.faceStatus = "挑战开始，请跟随每个表情做动作";
      // 顶栏 30 秒为展示用总时长(10×3 秒)；真正切换节奏由每轮定时器控制
      this.timer = setInterval(() => {
        if (this.remaining > 0) this.remaining -= 1;
        else {
          clearInterval(this.timer);
          this.timer = null;
        }
      }, 1000);
      this.playRound(0);
    },
    // 第 i 张：展示表情 → 70% 时刻自动拍照评分 → ROUND_MS 后切下一张
    playRound(i) {
      if (this.stage !== "playing") return;
      if (i >= TOTAL_ROUNDS) {
        this.finishRounds();
        return;
      }
      this.roundIndex = i;
      const ex = this.expressionList[i];
      this.currentExpression = ex;
      this.lastExpression = ex;
      this.faceStatus = `第 ${i + 1}/${TOTAL_ROUNDS} 张：模仿「${ex.name}」，即将自动拍照`;
      this._captureTimer = setTimeout(() => {
        this._captureTimer = null;
        if (i === this.roundIndex && this.roundScores[i] == null)
          this.fireCapture(i);
      }, Math.max(60, Math.round(ROUND_MS * 0.7)));
      this._roundTimer = setTimeout(() => {
        this._roundTimer = null;
        this.playRound(i + 1);
      }, ROUND_MS);
    },
    // 对第 i 张拍照并 AI 评分(结果异步落库,不阻塞切换)
    fireCapture(i) {
      if (this.roundScores[i] != null) return;
      this._busy = (this._busy || 0) + 1;
      this.isScoring = true;
      const ex = this.expressionList[i];
      this.faceStatus = `第 ${i + 1}/${TOTAL_ROUNDS} 张已拍照，AI 评分中...`;
      this.captureScore(ex)
        .then(score => {
          this.roundScores[i] = score;
          this.liveScore = score.total;
          this.metrics = {
            eyes: Math.round(score.eyes),
            mouth: Math.round(score.mouth),
            brows: Math.round(score.brows)
          };
          this.faceStatus = `第 ${i + 1}/${TOTAL_ROUNDS} 张 AI 得分 ${score.total} 分`;
        })
        .finally(() => {
          this._busy = Math.max(0, (this._busy || 1) - 1);
          if (!this._busy) this.isScoring = false;
        });
    },
    // 用户手动点击：跳过等待立即给当前这张拍照评分
    forceCaptureNow() {
      if (
        this.stage !== "playing" ||
        this.prepCount > 0 ||
        this.roundIndex < 0 ||
        this.isScoring ||
        this._busy > 0
      )
        return;
      if (!this.cameraReady) {
        this.retryStartCamera();
        return;
      }
      const i = this.roundIndex;
      if (this._captureTimer) {
        clearTimeout(this._captureTimer);
        this._captureTimer = null;
      }
      if (this.roundScores[i] == null) this.fireCapture(i);
    },
    async finishRounds() {
      this.faceStatus = "10 张已拍完，正在统计最终得分...";
      const deadline = Date.now() + 10000;
      while (
        (this._busy > 0 || this.roundScores.some(s => s == null)) &&
        Date.now() < deadline
      ) {
        await new Promise(resolve => setTimeout(resolve, 60));
      }
      const sum = arr => arr.reduce((acc, n) => acc + (Number(n) || 0), 0);
      const eyes = this.roundScores.map(s => (s ? s.eyes : 0));
      const mouth = this.roundScores.map(s => (s ? s.mouth : 0));
      const brows = this.roundScores.map(s => (s ? s.brows : 0));
      const total = this.roundScores.map(s => (s ? s.total : 0));
      const avg = Math.max(0, Math.min(100, sum(total) / TOTAL_ROUNDS));
      this.resultMetrics = {
        eyes: Math.round(sum(eyes) / TOTAL_ROUNDS),
        mouth: Math.round(sum(mouth) / TOTAL_ROUNDS),
        brows: Math.round(sum(brows) / TOTAL_ROUNDS)
      };
      const cm = this.commentFor(avg);
      this.resultComment = cm.title;
      this.resultTip = cm.tip;
      this.finalScore = avg.toFixed(1);
      this.clearTimers();
      this.stopCamera();
      const record = parseFloat(this.finalScore);
      if (record > this.bestScore) {
        this.bestScore = record;
        window.localStorage.setItem("face-game-best", String(record));
      }
      this.stage = "result";
    },
    commentFor(avg) {
      if (avg >= 90)
        return { title: "表情大师", tip: `10 张平均 ${avg.toFixed(1)} 分，几乎没有破绽，天生的表情帝！` };
      if (avg >= 80)
        return { title: "模仿达人", tip: `10 张平均 ${avg.toFixed(1)} 分，动作到位、张力十足。` };
      if (avg >= 70)
        return { title: "稳定发挥", tip: `10 张平均 ${avg.toFixed(1)} 分，大部分表情模仿得很像。` };
      if (avg >= 60)
        return { title: "渐入佳境", tip: `10 张平均 ${avg.toFixed(1)} 分，再放开一点会更好。` };
      return { title: "再接再厉", tip: `10 张平均 ${avg.toFixed(1)} 分，多试几次，把动作做得更夸张些。` };
    },
    _warnFallback() {
      if (this._fallbackWarned) return;
      this._fallbackWarned = true;
      showToast("AI 服务暂不可用，本次使用本地模拟评分");
    },
    _grabFrame() {
      const video = this.$refs.cameraVideo;
      if (!video || !video.videoWidth || !video.videoHeight) return null;
      try {
        const canvas = document.createElement("canvas");
        const maxWidth = 1280;
        const scale = Math.min(1, maxWidth / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const context = canvas.getContext("2d");
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        return canvas;
      } catch (error) {
        return null;
      }
    },
    // 单张 AI 评分：优先走真实接口(?ai=off 跳过)。首次失败即标记回退,
    // 本局后续张直接本地模拟,避免后端不可达时每张都等超时(10 张拖到几十秒)
    async captureScore(ex) {
      const canvas = this._grabFrame();
      if (canvas && !AI_OFF && !this._fallbackWarned) {
        try {
          const blob = await new Promise(resolve =>
            canvas.toBlob(resolve, "image/jpeg", 0.88)
          );
          if (!blob) throw new Error("照片生成失败");
          const apiBase = (
            process.env.VUE_APP_FACE_API ||
            process.env.VUE_APP_BASE_API ||
            ""
          ).replace(/\/$/, "");
          const formData = new FormData();
          formData.append("image", blob, `face-${Date.now()}.jpg`);
          formData.append("expression", ex.name);
          formData.append("action", ex.action);
          formData.append("level", ex.level);
          const ctrl = new AbortController();
          const abort = setTimeout(() => ctrl.abort(), 2500);
          const response = await fetch(`${apiBase}/api/face/score`, {
            method: "POST",
            body: formData,
            signal: ctrl.signal
          });
          clearTimeout(abort);
          const payload = await response.json();
          if (!response.ok || payload.success === false) {
            throw new Error(payload.msg || payload.message || "AI 评分失败");
          }
          const result = payload.data || payload;
          const score = result.score || {};
          if (result.faceDetected === false || result.singleFace === false) {
            // 拍到但没检测到人脸：这张记 0 分
            return { eyes: 0, mouth: 0, brows: 0, total: 0 };
          }
          // 严格系数：挑战级压得更狠；总分按 40/35/25 权重重算，不直接采信后端 total
          const k = STRICT_FACTOR[ex.level] || STRICT_FACTOR.进阶;
          const clamp = v => Math.max(0, Math.min(100, v));
          const eyes = clamp(Math.round((Number(score.eyes) || 0) * k));
          const mouth = clamp(Math.round((Number(score.mouth) || 0) * k));
          const brows = clamp(Math.round((Number(score.brows) || 0) * k));
          const total =
            Math.round((eyes * 0.4 + mouth * 0.35 + brows * 0.25) * 10) / 10;
          return { eyes, mouth, brows, total };
        } catch (error) {
          this._warnFallback();
        }
      }
      return this._simulateScore(ex);
    },
    // 本地模拟（回退/演示）：原始 50~99，再按表情难度乘严格系数（与真实接口同一套系数）
    _simulateScore(ex) {
      const k = STRICT_FACTOR[ex && ex.level] || STRICT_FACTOR.进阶;
      const raw = () => 50 + Math.round(Math.random() * 49);
      const eyes = Math.round(raw() * k);
      const mouth = Math.round(raw() * k);
      const brows = Math.round(raw() * k);
      const total =
        Math.round((eyes * 0.4 + mouth * 0.35 + brows * 0.25) * 10) / 10;
      return { eyes, mouth, brows, total };
    },
    backHome() {
      this.clearTimers();
      this.stopCamera();
      this.stage = "ready";
      this.remaining = 30;
      this.cameraError = "";
      this.cameraReady = false;
      this.isScoring = false;
      this.faceStatus = "准备开始挑战";
    }
  }
};
</script>

<style scoped lang="less" src="./index.less"></style>
